import { createSignal, untrack } from "solid-js";
import type { RecentRef } from "../utils/paletteResults";
import { currentUser } from "./currentUserStore";

/**
 * Recently visited work items and documents, feeding the command palette's
 * empty state (FLOW-800). Kept outside any component, mirrored into
 * localStorage, same pattern as `sidebarStore`.
 *
 * Stamped with `userId` (FLOW-800 security review) so a second user on the
 * same browser never sees the first user's recents: the storage key is
 * origin-scoped, not user-scoped, and nothing clears it on logout.
 */
export interface RecentEntry extends RecentRef {
  projectId: string;
  userId: string;
  visitedAt: number;
}

const STORAGE_KEY = "flowicity_recents";
const MAX_ENTRIES = 50;
const DEFAULT_LIMIT = 5;

const VALID_KINDS = new Set<RecentRef["kind"]>(["work_item", "document"]);

/** Guards against a malformed or hand-inflated `localStorage` value crashing
 * every reader of `recentEntries` (FLOW-800 security review). */
function isRecentEntry(value: unknown): value is RecentEntry {
  if (typeof value !== "object" || value === null) return false;
  const e = value as Record<string, unknown>;
  return (
    typeof e.kind === "string" &&
    VALID_KINDS.has(e.kind as RecentRef["kind"]) &&
    typeof e.id === "string" &&
    typeof e.projectId === "string" &&
    typeof e.userId === "string"
  );
}

function read(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecentEntry).slice(0, MAX_ENTRIES);
  } catch {
    return [];
  }
}

const [recentEntries, setRecentEntries] = createSignal<RecentEntry[]>(read());

export function recordVisit(
  kind: RecentEntry["kind"],
  id: string,
  projectId: string,
): void {
  // untrack both reads: recordVisit is typically called from a caller's
  // createEffect (e.g. a detail page's "record this visit" effect).
  // `recentEntries` needs it because the write below would otherwise
  // immediately re-trigger the effect — an infinite loop. `currentUser`
  // needs it because it is not set-once-forever: AppShell re-publishes a
  // fresh object on every WS reconnect, even for the same user, and a
  // tracked read would subscribe the effect to it — re-firing on every
  // reconnect and bumping the item back to the front of the list with a
  // new `visitedAt`.
  const userId = untrack(currentUser)?.id;
  if (!userId) return;

  const withoutExisting = untrack(recentEntries).filter(
    (e) => !(e.kind === kind && e.id === id),
  );
  const entries = [
    { kind, id, projectId, userId, visitedAt: Date.now() },
    ...withoutExisting,
  ].slice(0, MAX_ENTRIES);

  setRecentEntries(entries);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Storage unavailable (private mode, quota) — in-memory state still works.
  }
}

export function recentsFor(
  projectId: string | undefined,
  limit: number = DEFAULT_LIMIT,
): RecentEntry[] {
  if (!projectId) return [];
  // untrack: see the comment in recordVisit — a WS-reconnect re-publish of
  // the same user would otherwise recompute every memo reading this (e.g.
  // CommandPalette's `recents`), firing a redundant resolve request.
  const userId = untrack(currentUser)?.id;
  if (!userId) return [];
  return recentEntries()
    .filter((e) => e.projectId === projectId && e.userId === userId)
    .slice(0, limit);
}

export { recentEntries };
